import { Box, Button, Link, Typography } from '@mui/material'
import { Download } from 'lucide-react'
import { useNavigate } from '../../lib/router'
import type { CheckSummary, Run } from '../../api/types'
import { useRepairAll } from '../../api/hooks'
import { apiUrl, download } from '../../api/client'
import { EudrTag, Eyebrow, Mono, ShareBar, Surface, TierPill } from '../../components/primitives'
import { formatDate, ha, num, pct, signed } from '../../lib/format'
import { tokens } from '../../theme'
import { byPanelOrder } from '../newrun/checkMeta'

/** Screen 1c — the report a compliance lead signs off from. */
export function ResultsReport({ run }: { run: Run }) {
  const navigate = useNavigate()
  const repairAll = useRepairAll(run.id)
  const summary = run.summary
  const t = summary?.totals
  const checks = Object.values(summary?.checks ?? {})
    .filter((c) => c.enabled)
    .sort((a, b) => (a.tier === b.tier ? byPanelOrder(a, b) : a.tier === 'compliance' ? -1 : 1))
  const worst = Math.max(1, ...checks.map((c) => c.plots))

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7, display: 'flex', flexDirection: 'column', gap: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 6 }}>
        <Box>
          <Eyebrow sx={{ mb: 2 }}>
            {[run.supplier_name, run.country_code, formatDate(run.finished_at ?? run.created_at)]
              .filter(Boolean)
              .join(' · ')}
          </Eyebrow>
          <Typography variant="h1">
            {num(t?.submittable ?? 0)} of {num(t?.plots ?? 0)} plots usable
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download size={16} strokeWidth={1.5} />}
            onClick={() =>
              download(`/runs/${run.id}/export.pdf`, `polygon-checks-${run.id.slice(0, 8)}.pdf`).catch(() =>
                window.open(apiUrl(`/runs/${run.id}/export.html`), '_blank'),
              )
            }
          >
            Report (PDF)
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 3 }}>
        <StatCard label="Polygons" value={num(t?.plots ?? 0)} sub={`${ha(t?.area_ha, 0)} total`} />
        <StatCard
          label="Rejected"
          value={num(t?.rejected ?? 0)}
          colour={tokens.color.danger}
          sub={`${pct(t?.plots ? ((t.rejected / t.plots) * 100) : 0)} · ${ha(t?.rejected_area_ha, 0)}`}
        />
        <StatCard
          label="Flagged"
          value={num(t?.flagged ?? 0)}
          colour={tokens.color.warning}
          sub={`${num(summary?.issues.repairable ?? 0)} auto-repairable`}
        />
        <StatCard
          label="vs last run"
          value={summary?.previous_run ? signed(summary.previous_run.delta) : '—'}
          colour={(summary?.previous_run?.delta ?? 0) <= 0 ? tokens.color.success : tokens.color.danger}
          sub={
            summary?.previous_run
              ? `issues since ${formatDate(summary.previous_run.created_at)}`
              : 'first run for this region'
          }
        />
      </Box>

      <Surface sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '112px minmax(0,1.1fr) minmax(0,2fr) 80px minmax(0,0.9fr) 104px',
            alignItems: 'center', columnGap: 4, px: 5,
          }}
        >
          {['Type', 'Check', 'Rule', 'Failing', 'Share', ''].map((h, i) => (
            <Typography
              key={h || i}
              variant="overline"
              sx={{ color: 'text.disabled', py: 3, textAlign: i === 3 ? 'right' : 'left' }}
            >
              {h}
            </Typography>
          ))}

          {checks.map((check) => (
            <CheckRow key={check.key} check={check} worst={worst} runId={run.id} onView={() => navigate(`/regions/${run.region_id}?view=table&check=${check.key}`)} onRepairAll={() => repairAll.mutate()} repairing={repairAll.isPending} />
          ))}
        </Box>
      </Surface>

      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        Counts are open issues against this run. Repairs, trims, edits and exclusions update them without a full
        re-run. Run <Mono sx={{ fontSize: 11 }}>{run.id}</Mono>.
      </Typography>
    </Box>
  )
}

function StatCard({ label, value, sub, colour }: { label: string; value: string; sub: string; colour?: string }) {
  return (
    <Surface sx={{ px: 5, py: 4 }}>
      <Eyebrow>{label}</Eyebrow>
      <Typography
        sx={{
          font: `500 28px/1.2 ${tokens.font.sans}`, letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums', mt: 1.5, color: colour ?? 'text.primary',
        }}
      >
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>
    </Surface>
  )
}

function CheckRow({
  check, worst, onView, onRepairAll, repairing,
}: {
  check: CheckSummary
  worst: number
  runId: string
  onView: () => void
  onRepairAll: () => void
  repairing: boolean
}) {
  const cell = { borderTop: `1px solid ${tokens.color.ink200}`, height: 44, display: 'flex', alignItems: 'center' }
  const regionScope = check.scope === 'region'

  return (
    <Box sx={{ display: 'contents' }}>
      <Box sx={cell}><TierPill tier={check.tier} /></Box>
      <Box sx={{ ...cell, gap: 1.5, fontWeight: 500, minWidth: 0 }}>
        <Typography sx={{ font: `500 13px ${tokens.font.sans}` }}>{check.label}</Typography>
        {check.eudr && <EudrTag />}
      </Box>
      <Box sx={{ ...cell, color: 'text.secondary', minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            fontFamily: regionScope ? tokens.font.mono : undefined,
            fontSize: regionScope ? 12 : undefined,
          }}
        >
          {check.error ?? check.rule}
        </Typography>
      </Box>
      <Box sx={{ ...cell, justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}>
        {regionScope ? (
          <Typography sx={{ font: `500 13px ${tokens.font.sans}`, color: check.passed ? tokens.color.success : tokens.color.warning }}>
            {check.passed ? 'Pass' : 'Flagged'}
          </Typography>
        ) : (
          <Typography sx={{ font: `500 13px ${tokens.font.sans}` }}>{num(check.plots)}</Typography>
        )}
      </Box>
      <Box sx={cell}>
        {!regionScope && <ShareBar value={(check.plots / worst) * 100} tone={check.tier} />}
      </Box>
      <Box sx={{ ...cell, justifyContent: 'flex-end' }}>
        {regionScope ? null : check.repairable && check.plots > 0 ? (
          <Link onClick={onRepairAll} sx={{ font: `500 13px ${tokens.font.sans}` }}>
            {repairing ? 'Repairing…' : 'Repair all'}
          </Link>
        ) : check.plots > 0 ? (
          <Link onClick={onView} sx={{ font: `500 13px ${tokens.font.sans}` }}>View →</Link>
        ) : null}
      </Box>
    </Box>
  )
}
